import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  RestartAlt as RestartAltIcon,
  HelpOutline as HelpIcon
} from '@mui/icons-material';
import SearchBar from './SearchBar';
import DataTable from './Components/DataTable';
import { useDashboardContext } from './DashboardContext';
import { marked } from 'marked';
import GPF2FCallsComponent from './EmployeeDashboard/GPF2FCallsComponent';
import ANPF2FCallsComponent from './EmployeeDashboard/ANPF2FCallsComponent';
import HospitalF2FCallsComponent from './EmployeeDashboard/HospitalF2FCallsComponent';
import ROIComponent from './EmployeeDashboard/ROIComponent';
import F2FCallsOverview from './EmployeeDashboard/F2FCallsOverview';
import ChatComponent from './Components/ChatComponent';
import rxaiIcon from './assets/rxaiIcon.png';
import StartNewChatButton from './Components/StartNewChatButton';

export default function EmployeeDashboard() {
  const { conversations, setConversation, chatIds, setChatId } = useDashboardContext();
  const conversation = conversations.byRep || [];
  const apiUrl = process.env.REACT_APP_API_URL;
  const chatWindowRef = useRef(null);

  // Tabs & rep selection
  const [tabIndex, setTabIndex] = useState(0);
  const [allReps, setAllReps] = useState([]);
  const [repTabIndex, setRepTabIndex] = useState(0);
  const [selectedRep, setSelectedRep] = useState('');

  // Chat/search state
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [followUpQuestions, setFollowUpQuestions] = useState([]);
  const loadingMessages = [
    'Analyzing your question...',
    'Gathering relevant information...',
    'Verifying details...',
  ];
  const [currentLoadingMessage, setCurrentLoadingMessage] = useState(0);

  // Spinner text rotation
  useEffect(() => {
    if (!loading) return;
    const iv = setInterval(() => {
      setCurrentLoadingMessage(i => (i + 1) % loadingMessages.length);
    }, 3000);
    return () => clearInterval(iv);
  }, [loading]);

  // Load reps on mount
  useEffect(() => {
    fetch(`${apiUrl}/api/get_all_reps`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        const reps = data.employee_names || [];
        setAllReps(reps);
        if (reps.length) setSelectedRep(reps[0]);
      })
      .catch(console.error);
  }, [apiUrl]);

  // Start chat when switching to By Rep
  useEffect(() => {
    if (tabIndex === 1 && selectedRep) {
      setFollowUpQuestions([]);
      setConversation('byRep', []);
      startNewChatForRep();
    }
  }, [tabIndex, selectedRep]);

  // Auto-scroll chat
  useEffect(() => {
    if (chatWindowRef.current) {
      chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
    }
  }, [conversation, loading]);

  const handleTabChange = (_, newIndex) => setTabIndex(newIndex);

  const handleRepTabChange = (_, newIndex) => setRepTabIndex(newIndex);

  const startNewChatForRep = async () => {
    try {
      const res = await fetch(`${apiUrl}/api/start_new_chat_employee`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ employee_name: selectedRep }),
      });
      const { thread_id } = await res.json();
      setChatId('byRep', thread_id);
    } catch {
      setConversation('byRep', [{ role: 'assistant', content: 'Failed to start chat. Please try again.' }]);
    }
  };

  const defaultQuestions = [
    'Show me how many packs of Simalvia were prescribed in the last 12 months?',
    'Show me a breakdown of my activity to prescribers in the last 12 months?',
  ];

  const handleSearch = async (searchQuery) => {
    if (!searchQuery.trim() || !selectedRep) return;
    setLoading(true);

    const userMsg = { role: 'user', content: searchQuery, timestamp: Date.now() };
    const conv = conversation.length ? [...conversation, userMsg] : [userMsg];
    setConversation('byRep', conv);

    try {
      const res = await fetch(`${apiUrl}/api/ask_employee_question`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ question: searchQuery, employee_name: selectedRep }),
      });
      const data = await res.json();
      if (data.answer.headers && data.answer.rows) {
        setConversation('byRep', [
          ...conv,
          { role: 'assistant', 
            content: { type: 'table', headers: data.answer.headers, rows: data.answer.rows },
            sql_query: data.sql_query,
            question: data.question,
            canDownload: data.can_download, 
            timestamp: Date.now()
          }
        ]);
      } else {
        setConversation('byRep', [
          ...conv,
          { role: 'assistant', content: `**RxAI:**\n${data.answer}`, sql_query: data.sql_query, question: data.question, canDownload: data.can_download, timestamp: Date.now() }
        ]);
      }

      if (data.graph_image) {
        setConversation('byRep', conv => [
          ...conv,
          {
            role: 'assistant',
            content: `**RxAI:**\n<h3>Graph</h3><img src="data:image/png;base64,${data.graph_image}" style="max-width:100%;" />`,
            sql_query: data.sql_query,
            question: data.question, 
            timestamp: Date.now()
          }
        ]);
      }

      setFollowUpQuestions(data.follow_up?.split('\n').filter(Boolean) || []);
    } catch (error) {
      setConversation('byRep', [
        ...conv,
        { role: 'assistant', content: `**RxAI:** Sorry, something went wrong. ${error}`, timestamp: Date.now() }
      ]);
    } finally {
      setLoading(false);
      setQuery('');
    }
  };

  const handleNewChat = () => {
    setConversation('byRep', []);
    startNewChatForRep();
  };

  return (
    <Box sx={{ p: 3, minHeight: '100vh', background: '#FFFFFF' }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 3, color: 'text.primary',letterSpacing: '0.5px', }}>
        Employee Dashboard
      </Typography>

      <Tabs value={tabIndex} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab label="Overview" />
        <Tab label="By Rep" disabled={!allReps.length} />
      </Tabs>

      {tabIndex === 0 && (
        <Grid container direction="column" spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <F2FCallsOverview title="GP F2F Calls (Last 12 months)" endpoint="/api/activity_gp_f2f_calls_all" />
          </Grid>
          <Grid item xs={12}>
            <F2FCallsOverview title="ANP F2F Calls (Last 12 months)" endpoint="/api/activity_anp_f2f_calls_all" />
          </Grid>
          <Grid item xs={12}>
            <F2FCallsOverview title="Hospital F2F Calls (Last 12 months)" endpoint="/api/activity_hospital_f2f_calls_all" />
          </Grid>
        </Grid>
      )}

      {tabIndex === 1 && (
        <Box>
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="rep-select-label">Select Sales Rep</InputLabel>
            <Select
              labelId="rep-select-label"
              value={selectedRep}
              label="Select Sales Rep"
              onChange={e => setSelectedRep(e.target.value)}
            >
              {allReps.map(rep => <MenuItem key={rep} value={rep}>{rep}</MenuItem>)}
            </Select>
          </FormControl>

          {selectedRep && (
            <>
              <Tabs value={repTabIndex} onChange={handleRepTabChange} sx={{ mb: 3 }}>
                <Tab label="Call rates" />
                <Tab label="Rx/Contact"/>
              </Tabs>
              {repTabIndex === 0 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={4}><GPF2FCallsComponent employeeName={selectedRep} /></Grid>
                  <Grid item xs={12} sm={4}><ANPF2FCallsComponent employeeName={selectedRep} /></Grid>
                  <Grid item xs={12} sm={4}><HospitalF2FCallsComponent employeeName={selectedRep} /></Grid>
                </Grid>
              )}

              {repTabIndex === 1 && (
                <ROIComponent employeeName={selectedRep} />
              )}

              <Card sx={{ mt: 4, borderRadius: 2, boxShadow: 3 }}>
                <CardContent>
                  <Box
                    ref={chatWindowRef}
                    sx={{ height: '50vh', overflowY: 'auto', p: 2, background: '#FFFFFF', borderRadius: 1 }}
                  >
                    {conversation.length === 0 && (
                      <Box sx={{ textAlign: 'center', mt: 10 }}>
                        <img src={rxaiIcon} style={{ width: 50, height: 50 }} />
                        <Typography variant="h6">Ask me about {selectedRep}’s activity!</Typography>
                        <Box
                          sx={{
                            mt: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                            alignItems: 'center'
                          }}
                        >
                          {defaultQuestions.map((question, idx) => (
                            <Button
                              key={idx}
                              variant="outlined"
                              color="secondary"
                              onClick={() => handleSearch(question)}
                              sx={{
                                textTransform: 'none',
                                fontFamily: 'Roboto, sans-serif',
                                width: '100%',
                                maxWidth: 500,
                              }}
                            >
                              {question}
                            </Button>
                          ))}
                        </Box>
                      </Box>
                    )}
                    {conversation.map((msg, i) => {
                      return <ChatComponent index={i} message={msg}/>
                    })}
                    {loading && (
                      <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                        <CircularProgress size={20}/>
                        <Typography>{loadingMessages[currentLoadingMessage]}</Typography>
                      </Box>
                    )}
                  </Box>

                  <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {followUpQuestions.map((q, idx) => (
                      <Tooltip key={idx} title={q}>
                        <IconButton onClick={() => handleSearch(q)}><HelpIcon/></IconButton>
                      </Tooltip>
                    ))}
                  </Box>

                  <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <SearchBar query={query} setQuery={setQuery} handleSearch={() => handleSearch(query)} loading={loading}/>
                    <StartNewChatButton handleNewChat={handleNewChat}/>
                  </Box>
                </CardContent>
              </Card>
            </>
          )}
        </Box>
      )}
    </Box>
  );
}